import {
  onValue,
  push,
  ref,
  remove,
  set,
  update,
  type Unsubscribe,
} from 'firebase/database';
import { getFirebaseDb } from '@/lib/firebase';
import type { ChatMessage, MessageSenderRole } from '@/types/support';

export async function sendChatMessage(opts: {
  requestId: string;
  senderId: string;
  senderRole: MessageSenderRole;
  text: string;
  type?: 'text' | 'system';
}): Promise<string> {
  const messagesRef = ref(getFirebaseDb(), `chatMessages/${opts.requestId}`);
  const msgRef = push(messagesRef);
  const messageId = msgRef.key!;
  const message: Omit<ChatMessage, 'messageId'> & { messageId: string } = {
    messageId,
    senderId: opts.senderId,
    senderRole: opts.senderRole,
    text: opts.text.trim(),
    createdAt: Date.now(),
    delivered: true,
    read: false,
    type: opts.type ?? 'text',
  };
  await set(msgRef, message);

  const chatRef = ref(getFirebaseDb(), `activeChats/${opts.requestId}`);
  try {
    if (opts.senderRole === 'customer') {
      await update(chatRef, { lastMessageAt: Date.now(), unreadAgent: Date.now() });
    } else if (opts.senderRole === 'agent') {
      await update(chatRef, { lastMessageAt: Date.now(), unreadCustomer: Date.now() });
    } else {
      await update(chatRef, { lastMessageAt: Date.now() });
    }
  } catch {
    // active chat may not exist yet for early system messages
  }

  return messageId;
}

export function subscribeMessages(
  requestId: string,
  cb: (messages: ChatMessage[]) => void,
): Unsubscribe {
  return onValue(ref(getFirebaseDb(), `chatMessages/${requestId}`), (snap) => {
    if (!snap.exists()) {
      cb([]);
      return;
    }
    const val = snap.val() as Record<string, ChatMessage>;
    const list = Object.values(val).sort((a, b) => a.createdAt - b.createdAt);
    cb(list);
  });
}

export async function markMessagesRead(requestId: string, readerRole: 'customer' | 'agent'): Promise<void> {
  const field = readerRole === 'customer' ? 'unreadCustomer' : 'unreadAgent';
  await update(ref(getFirebaseDb(), `activeChats/${requestId}`), { [field]: 0 }).catch(() => undefined);
}

export async function setTyping(requestId: string, uid: string, isTyping: boolean): Promise<void> {
  const typingRef = ref(getFirebaseDb(), `typing/${requestId}/${uid}`);
  if (!isTyping) {
    await remove(typingRef);
    return;
  }
  await set(typingRef, { uid, isTyping: true, updatedAt: Date.now() });
}

export function subscribeTyping(
  requestId: string,
  cb: (typingUids: string[]) => void,
): Unsubscribe {
  return onValue(ref(getFirebaseDb(), `typing/${requestId}`), (snap) => {
    if (!snap.exists()) {
      cb([]);
      return;
    }
    const val = snap.val() as Record<string, { uid: string; isTyping: boolean; updatedAt: number }>;
    const now = Date.now();
    cb(
      Object.values(val)
        .filter((t) => t.isTyping && now - t.updatedAt < 8000)
        .map((t) => t.uid),
    );
  });
}
